import React, { useState } from "react";
import { Button, Drawer, Flex, LegacyDatePicker, LegacyForm, Typography } from "@payping/web-components";
import { useNavigate } from "react-router-dom";
import { useAppSelector, useAppTranslation } from "@/utils";
import { Close } from "@payping/icons";
import EditActionButtons from "@/components/mobile/settings/EditActionButtons";
import { SDKDateObject, dateFormats, isBeforeToday, toJalali, toUtc } from "@payping/utils/dateUtils";
import { DashboardRoutes } from "@/router/constant";
import { isMobilePersonalRegisterURL } from "@/utils/isRegisterURL";
import { isPersonalDisable } from "@/mobile/Settings/Personal/Edit/isPersonalDisable";
import { useHandleNavigationURL } from "@/mobile/Settings/Personal/utils";
import { useImmerAtom } from "jotai-immer";
import { settingsPersonalAtom } from "../../state";
import { selectAtom } from "jotai/utils";
import { useAtom } from "jotai";

const mobileFormEditAtom = selectAtom(settingsPersonalAtom, (val) => val.mobileFormEdit);

const MobileEditPersonalBirthDate: React.FC = () => {
  const isRegisterURL = isMobilePersonalRegisterURL();
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [form] = LegacyForm.useForm();
  const navigate = useNavigate();
  const [, setSettingsPersonalState] = useImmerAtom(settingsPersonalAtom);

  const [mobileFormEditState] = useAtom(mobileFormEditAtom);
  const birthDateApi = useAppSelector((state) => state.user.personalInfo?.birthDate);
  const { t } = useAppTranslation();

  const [birthDate, setBirthDate] = useState<SDKDateObject>();
  const utcDateApi =
    birthDateApi && birthDateApi !== "0001-01-01T00:00:00"
      ? (toJalali({
          convertTime: false,
          date: birthDateApi,
          dateFormat: dateFormats.standard.utcDateTime,
          toFormat: dateFormats.standard.utcDate,
        }).object as SDKDateObject)
      : undefined;
  const utcDateRedux =
    mobileFormEditState.birthDate && mobileFormEditState.birthDate !== "0001-01-01T00:00:00"
      ? (toJalali({
          convertTime: false,
          date: mobileFormEditState.birthDate,
          dateFormat: dateFormats.standard.utcDateTime,
          toFormat: dateFormats.standard.utcDate,
        }).object as SDKDateObject)
      : undefined;

  async function saveToRedux() {
    const birthDateSting = toUtc({
      convertTime: false,
      date: birthDate,
      toFormat: dateFormats.standard.utcDate,
    }).string;
    setSettingsPersonalState((draft) => {
      // eslint-disable-next-line no-param-reassign
      draft.mobileFormEdit.birthDate = birthDateSting;
    });
    await setIsDrawerOpen(false);
    if (isRegisterURL) navigate(DashboardRoutes.register.mEditPersonal.birthCertificateNumber.url);
    else navigate(DashboardRoutes.settings.personal.root.url);
  }

  // Custom Hooks
  const { handleNavigationURL } = useHandleNavigationURL();

  return (
    <>
      <Drawer
        open={isDrawerOpen}
        title={`${t("edit")} / ${t("birthDate")}`}
        height={"100%"}
        placement={"bottom"}
        closeIcon={false}
        extra={<Button type={"text"} icon={<Close width={14} height={14} />} onClick={handleNavigationURL} />}
      >
        <Flex vertical>
          <LegacyForm onFinish={saveToRedux} form={form} initialValues={{ birthDate: utcDateRedux ?? utcDateApi }}>
            <LegacyForm.Item
              name="birthDate"
              rules={[
                { required: true },
                {
                  validator(_, value) {
                    if (value && !isBeforeToday(value)) {
                      return Promise.reject(new Error(t("profile.birthDateValidation")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <LegacyDatePicker
                className={"tw:w-full"}
                disabledDate={(date) => !isBeforeToday(date)}
                defaultValue={birthDate}
                onChange={(date) => {
                  if (date instanceof Array) {
                    setBirthDate(date[0]);
                    return;
                  }
                  setBirthDate(date);
                }}
                dateOrder={["YearPicker", "MonthPicker", "DatePicker"]}
                disabled={isPersonalDisable()}
              />
            </LegacyForm.Item>
          </LegacyForm>

          <Typography.Text type={"secondary"}>
            {t("settings.personal.instructions.nationalIdAndBirthdate")}
          </Typography.Text>
        </Flex>
      </Drawer>
      <EditActionButtons onAccept={form.submit} />
    </>
  );
};

export default MobileEditPersonalBirthDate;
